import React, { useEffect, useRef } from 'react';
import { X, Package, Users, CheckCircle, Truck, HelpCircle } from 'lucide-react';
import './HelpModal.css';

export function HelpModal({ isOpen, onClose }) {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="help-modal-overlay-custom" onClick={handleOverlayClick}>
      <div className="help-modal-container" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>Help & Instructions</h2>
          <button className="help-modal-close" onClick={onClose} aria-label="Close Help">
            <X size={24} />
          </button>
        </div>
        <div className="help-modal-body">
          <section className="help-section">
            <h3><Package size={20} className="help-icon-inline" /> Managing Packages</h3>
            <p><strong>View Packages:</strong> Navigate to "Packages" in the sidebar to see all shipments.</p>
            <p><strong>Filter Packages:</strong> Use the filter buttons (All, Incoming, Assigned, In Transit, Completed) to view specific package statuses.</p>
            <p><strong>Search:</strong> Use the search bar to find packages by ID, warehouse, destination, sender, or recipient.</p>
            <p><strong>View Details:</strong> Click on any package row to see full details including sender, recipient, and tracking information.</p>
          </section>

          <section className="help-section">
            <h3><Users size={20} className="help-icon-inline" /> Registering a New Courier</h3>
            <p><strong>Step 1:</strong> Go to "Couriers" → "Couriers List" in the sidebar.</p>
            <p><strong>Step 2:</strong> Click the "Register New Courier" button (typically at the top right).</p>
            <p><strong>Step 3:</strong> Fill in courier details: name, contact information, vehicle assignment, and documents.</p>
            <p><strong>Step 4:</strong> Submit the form. The courier will appear in the Couriers List with "Offline" status initially.</p>
          </section>

          <section className="help-section">
            <h3><CheckCircle size={20} className="help-icon-inline" /> Assigning a Package to a Courier</h3>
            <p><strong>Method 1 - From Packages:</strong> Go to Packages, click on an unassigned package, select "Assign Courier" and choose from available couriers.</p>
            <p><strong>Method 2 - From Couriers:</strong> Go to Couriers List, select a courier, and assign unassigned packages directly to them.</p>
          </section>

          <section className="help-section">
            <h3><Truck size={20} className="help-icon-inline" /> Managing Vehicles</h3>
            <p><strong>Add Vehicle:</strong> Go to "Vehicles", click "Add Vehicle", enter license plate, type, and capacity.</p>
            <p><strong>Assign to Courier:</strong> Vehicles can be linked to specific couriers from the Couriers section.</p>
          </section>

          <section className="help-section help-section-support">
            <h3><HelpCircle size={20} className="help-icon-inline" /> Need more help?</h3>
            <p>If you encounter a system error or need feature assistance, please contact the IT support team at <a href="mailto:support@smartcargo.az">support@smartcargo.az</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
